import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api';

/**
 * RTK Query API for File Audit Logs
 *
 * Provides access to file operation audit logs with filtering and pagination.
 * Follows DRF pagination response structure.
 *
 * Endpoints:
 * - getAuditLogs: List audit logs with optional filters
 */
export const auditLogsApi = createApi({
  reducerPath: 'auditLogsApi',
  baseQuery,
  tagTypes: ['AuditLogs'],
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    /**
     * Get audit logs with optional filtering
     *
     * @param {Object} params - Query parameters
     * @param {string} [params.action] - Filter by action type (CREATE, DELETE, MOVE)
     * @param {string} [params.from] - Start date (YYYY-MM-DD)
     * @param {string} [params.to] - End date (YYYY-MM-DD)
     * @param {string} [params.preset] - Date preset (7, 30, 90 days)
     * @param {number} [params.page=1] - Page number
     * @param {number} [params.page_size=20] - Items per page
     * @param {string} [params.order_by=-timestamp] - Sort field (prefix with - for descending)
     *
     * @returns {Object} DRF paginated response
     * {
     *   count: number,
     *   next: string|null,
     *   previous: string|null,
     *   results: Array<{
     *     id: number,
     *     user: { id: number, full_name: string },
     *     action: 'CREATE'|'DELETE'|'MOVE',
     *     description: string,
     *     file_size_kb: number,
     *     timestamp: string (ISO 8601)
     *   }>
     * }
     */
    getAuditLogs: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();

        if (params.action) queryParams.append('action', params.action);
        if (params.from) queryParams.append('from', params.from);
        if (params.to) queryParams.append('to', params.to);
        if (params.preset) queryParams.append('preset', params.preset);
        if (params.page) queryParams.append('page', params.page);
        if (params.page_size) queryParams.append('page_size', params.page_size);
        if (params.order_by) queryParams.append('order_by', params.order_by);

        const queryString = queryParams.toString();
        return `/api/v1/file-audit-logs/${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['AuditLogs'],
    }),
  }),
});

export const { useGetAuditLogsQuery } = auditLogsApi;
